import type { DataSourcesListCollection } from '../types';

import { mockAssignmentDataSource } from './mockAssignmentDataSource';

/** Demo linkage until assignment connections API supports filtering by data source key. */
export const LINKED_ASSIGNMENT_DATA_SOURCE_KEY = 'assignment-demo';
export const LINKED_ASSIGNMENT_EXPERIMENT_KEY = 'devins-writing-assistant';

/** Stable mock catalog for the assignments tab until backend APIs exist. */
const MOCK_ASSIGNMENT_DATA_SOURCE_SEEDS = [
  { key: 'assignment-prod-snowflake', name: 'Production assignments', archived: false, experimentsCount: 12 },
  { key: 'assignment-staging-databricks', name: 'Staging assignments', archived: false, experimentsCount: 5 },
  { key: 'assignment-dev-bigquery', name: 'Development assignments', archived: false, experimentsCount: 0 },
  { key: 'assignment-holdout-snowflake', name: 'Holdout experiment source', archived: false, experimentsCount: 8 },
  { key: LINKED_ASSIGNMENT_DATA_SOURCE_KEY, name: 'Demo assignment source', archived: false, experimentsCount: 1 },
  { key: 'assignment-legacy-redshift', name: 'Legacy warehouse assignments', archived: true, experimentsCount: 3 },
  { key: 'assignment-deprecated', name: 'Deprecated assignment source', archived: true, experimentsCount: 0 },
] as const;

function integrationKeyFromAssignmentSourceKey(dataSourceKey: string): string | undefined {
  if (dataSourceKey.includes('snowflake')) {
    return 'snowflake-experimentation';
  }
  if (dataSourceKey.includes('databricks')) {
    return 'databricks-experimentation';
  }
  if (dataSourceKey.includes('redshift')) {
    return 'redshift-experimentation';
  }
  if (dataSourceKey.includes('bigquery')) {
    return 'bigquery-experimentation';
  }

  return undefined;
}

const mockAssignmentCatalog = MOCK_ASSIGNMENT_DATA_SOURCE_SEEDS.map((seed) => {
  let builder = mockAssignmentDataSource()
    .withKey(seed.key)
    .withName(seed.name)
    .withArchived(seed.archived)
    .withExperimentsCount(seed.experimentsCount);

  const integrationKey = integrationKeyFromAssignmentSourceKey(seed.key);
  if (integrationKey) {
    builder = builder.withIntegrationKey(integrationKey);
  }

  if (seed.key === LINKED_ASSIGNMENT_DATA_SOURCE_KEY) {
    return builder.withEnvironmentKey('production').withExperimentKeys([LINKED_ASSIGNMENT_EXPERIMENT_KEY]).create();
  }

  return builder.create();
});

/** Stub for getAssignmentDataSourceByKeyQuery until assignment backend APIs exist. */
export function getMockAssignmentDataSourceByKey(dataSourceKey: string) {
  return mockAssignmentCatalog.find((item) => item.key === dataSourceKey);
}

export function getMockAssignmentDataSources({ archived }: { archived: boolean }): DataSourcesListCollection {
  const items = mockAssignmentCatalog.filter((item) => Boolean(item.archived) === archived);

  return {
    items,
    totalCount: items.length,
  };
}
